import React, { useState } from "react";

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  privacy: boolean;
}

interface FormErrors {
  [key: string]: string;
}

export const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    privacy: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "El email no es válido";
    }

    if (formData.phone && !/^[+]?[\d\s-]{9,}$/.test(formData.phone)) {
      newErrors.phone = "El teléfono no es válido";
    }

    if (!formData.message.trim()) {
      newErrors.message = "El mensaje es obligatorio";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "El mensaje debe tener al menos 10 caracteres";
    }

    if (!formData.privacy) {
      newErrors.privacy = "Debes aceptar la política de privacidad";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const ajaxUrl =
        window.manosactivasAjax?.ajaxurl || "/wp-admin/admin-ajax.php";
      const nonce = window.manosactivasAjax?.nonce || "";

      const { privacy, ...fields } = formData;
      const response = await fetch(ajaxUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          action: "manosactivas_contact_form",
          nonce: nonce,
          ...fields,
          privacy: privacy ? "1" : "0",
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          privacy: false,
        });
      } else {
        console.error("Form submission error:", result);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="md:col-span-1">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-800 mb-2"
          >
            Nombre completo *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-transparent text-gray-900 ${
              errors.name ? "border-accent" : "border-gray-200"
            } focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="Tu nombre"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-accent">{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div className="md:col-span-1">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-800 mb-2"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-transparent text-gray-900 ${
              errors.email ? "border-accent" : "border-gray-200"
            } focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="tu@email.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-accent">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div className="md:col-span-1">
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-800 mb-2"
          >
            Teléfono
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-transparent text-gray-900 ${
              errors.phone ? "border-accent" : "border-gray-200"
            } focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="+34 600 000 000"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-accent">{errors.phone}</p>
          )}
        </div>

        {/* Subject */}
        <div className="md:col-span-1">
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-800 mb-2"
          >
            Tema de consulta
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border bg-transparent text-gray-900 border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            placeholder="Ej: Alimentación, Autonomía, TEA..."
            maxLength={100}
          />
        </div>

        {/* Message */}
        <div className="md:col-span-2">
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-800 mb-2"
          >
            Mensaje *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            className={`w-full px-4 py-3 rounded-xl border bg-transparent text-gray-900 ${
              errors.message ? "border-accent" : "border-gray-200"
            } focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none`}
            placeholder="Cuéntanos en qué podemos ayudarte..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-accent">{errors.message}</p>
          )}
        </div>

        {/* Privacy */}
        <div className="md:col-span-2">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              name="privacy"
              checked={formData.privacy}
              onChange={handleChange}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors mt-1">
              He leído y acepto la{" "}
              <a
                href="/politica-privacidad"
                className="mt-1 text-primary-deep hover:underline"
              >
                Política de Privacidad
              </a>{" "}
              y el tratamiento de mis datos personales. *
            </span>
          </label>
          {errors.privacy && (
            <p className="mt-1 text-sm text-accent">{errors.privacy}</p>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="mt-8">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Enviando...
            </span>
          ) : (
            "Enviar mensaje"
          )}
        </button>
      </div>

      {/* Status messages */}
      {submitStatus === "success" && (
        <div className="mt-6 p-4 rounded-xl bg-secondary/10 text-secondary-dark">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">¡Mensaje enviado correctamente!</span>
          </div>
          <p className="mt-1 text-sm">
            Nos pondremos en contacto contigo lo antes posible.
          </p>
        </div>
      )}

      {submitStatus === "error" && (
        <div className="mt-6 p-4 rounded-xl bg-accent/10 text-accent-dark">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">Error al enviar el mensaje</span>
          </div>
          <p className="mt-1 text-sm">
            Por favor, inténtalo de nuevo o escribe directamente a{" "}
            <a
              href="mailto:info@manosactivas.es"
              className="font-semibold underline"
            >
              info@manosactivas.es
            </a>
          </p>
        </div>
      )}
    </form>
  );
};
